import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import TableRowCell
, {
  getStyleFromCellType,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView/CollapsableTable/TableRowCell';
import TableRowLabel
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView/CollapsableTable/TableRowLabel';
import TableRow from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableRow';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import TableCell from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableCell';
import IconButton from '@material-ui/core/IconButton';
import { Collapse, Fade } from '@mui/material';
import Table from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/Table';
import TableBody
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView/CollapsableTable/TableBody';
import {
  RESOURCE_TYPES,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/Allocations/TableView';

// making the table header sticky instead
const STICKY_ROW_LABELS = [
];

const styles = (theme) => ({
  root: {
    padding: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.common.white,
    minHeight: 55,
  },
  midLevel: {
    backgroundColor: theme.palette.facet.accentBlue5,
  },
  bottomLevel: {
    backgroundColor: theme.palette.facet.accentBlue17,
  },
  totalLevel: {
    backgroundColor: theme.palette.facet.accentGreen10,
  },
  subtotalLevel: {
    backgroundColor: theme.palette.facet.accentGreen10,
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'all 0.5s ease-out',
  },
  rowSetExpanded: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[100],
  },
  labelCell: {
    padding: theme.spacing(1),
    zIndex: 100,
    position: 'sticky',
    left: 0,
    width: '200px',
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white,
  },
  collapsedRowLabelCell: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
  },
  labelCellInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    margin: 0,
  },
  dropdownButton: {
    flexBasis: '25%',
    flexShrink: 0,
    flexGrow: 0,
  },
  rowLabel: {
    flexBasis: '75%',
    flexShrink: 0,
    flexGrow: 0,
  },
  rowLabelFullWidth: {
    flexBasis: '100%',
    flexShrink: 0,
    flexGrow: 0,
  },
  nestedRowSummarySet: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  collapsableTable: {
    width: '100%',
    borderCollapse: 'separate', // <--- Prevents sticky border from being collapsed on scroll
  },
  stickyRow: {
    position: 'sticky',
    zIndex: 102,
  },
  stickyCashOnHand: {
    top: 0,
  },
  stickyNetChange: {
    top: 55,
  },
});

const CollapsableTableRow = ({
  data,
  label,
  altLabel,
  subLabel,
  className,
  classes,
  rowResource,
  rowSets,
  rowSummarySet,
  isRowSet,
  cellType,
  rowDetailsUrl,
  onCellFormSubmitOverride,
  onLockRow,
}) => {
  const [open, setOpen] = useState(false);

  // Determine if row as row / row summary sets
  const hasRowSets = rowSets && rowSets.length >= 1;
  const hasRowSummarySets = rowSummarySet && rowSummarySet.length >= 1;

  // Handles expanding nested row set data
  const handleRowClick = () => {
    if (hasRowSets || hasRowSummarySets) setOpen(!open);
  };

  const isHighlightedByType = (highlightType, cell) => {
    return cell.cellResource.resourceType === highlightType;
  };

  const { className: rowClassName } = getStyleFromCellType(cellType);

  return (
    <React.Fragment>
      <TableRow
        className={classNames(classes.root, className, {
          [classes.rowSetExpanded]: open && isRowSet && !cellType,
          [classes.clickableRow]: hasRowSets || hasRowSummarySets,
          [classes.stickyRow]: STICKY_ROW_LABELS.includes(label),
          [classes.stickyCashOnHand]: label === STICKY_ROW_LABELS[0],
          [classes.stickyNetChange]: label === STICKY_ROW_LABELS[1],
          [classes.midLevel]: rowClassName === 'midLevel',
          [classes.bottomLevel]: rowClassName === 'bottomLevel',
          [classes.totalLevel]: rowClassName === 'totalLevel',
          [classes.subtotalLevel]: rowClassName === 'subtotalLevel',
        })}
        onClick={() => handleRowClick()}
        test={`facet-plan-allocation-collapsable-table-row-${label}`}
      >
        <TableCell className={classNames(classes.labelCell, {
          [classes.collapsedRowLabelCell]: isRowSet,
        })}
        >
          <div className={classes.labelCellInner}>
            <div className={classes.dropdownButton}>
              {(hasRowSets || hasRowSummarySets) && (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>)
          }
            </div>
            {label && (
              <TableRowLabel
                className={classNames({
                  [classes.rowLabel]: (hasRowSets || hasRowSummarySets),
                  [classes.rowLabelFullWidth]: (!hasRowSets && !hasRowSummarySets),
                })}
                label={label}
                altLabel={altLabel}
                subLabel={subLabel}
                isRowSet={isRowSet}
                cellType={cellType}
                rowDetailsUrl={rowDetailsUrl}
                rowLocked={data?.every(cell => cell.cellResource.id().includes('total') || cell.cellResource.isTruthy('userModified'))}
                onLockRow={rowResource.isTruthy('rowIsLockable') ? onLockRow : null}
                rowResource={rowResource}
                test={`facet-plan-allocation-collapsable-table-row-label-${label}`}
              />
            )}
          </div>
        </TableCell>
        {data?.map(cell => {
          const highlightedByType = {};
          for (let j = 0; j < Object.keys(RESOURCE_TYPES).length; j++) {
            const hType = RESOURCE_TYPES[Object.keys(RESOURCE_TYPES)[j]];
            const matches = isHighlightedByType(hType, cell);
            highlightedByType[hType] = {
              matches,
            };
          }

          return (
            <TableRowCell
              key={cell.id}
              data={cell}
              highlightedByType={highlightedByType}
              cellType={cell.cellType || cellType}
              test={`facet-plan-allocation-table-row-cell-${label}`}
              onCellFormSubmitOverride={onCellFormSubmitOverride}
            />
          );
        })}
      </TableRow>

      {/* --- Collapsable Row Sets / Row Summary Sets --- */}
      {(hasRowSets || hasRowSummarySets) && (
        <TableCell style={{ padding: 0 }} colSpan={data.length + 1}>
          <Collapse
            in={open}
            timeout={'auto'}
            unmountOnExit
            className={classNames({
              [classes.rowSetExpanded]: open,
            })}
          >
            <Fade
              in={open}
              timeout={350}
            >
              <Table className={classes.collapsableTable}>
                {hasRowSets && (
                <TableBody
                  data={rowSets}
                  isRowSet
                />)
              }
                {hasRowSummarySets && (
                <TableBody
                  className={classNames(classes.nestedRowSummarySet)}
                  data={rowSummarySet}
                  isRowSet
                />)
              }
              </Table>
            </Fade>
          </Collapse>
        </TableCell>)}
    </React.Fragment>
  );
};

CollapsableTableRow.defaultProps = {
  className: '',
  data: null,
  label: null,
  altLabel: null,
  subLabel: null,
  rowSets: null,
  rowSummarySet: null,
  isRowSet: false,
  cellType: null,
  rowDetailsUrl: null,
  onCellFormSubmitOverride: null,
  onLockRow: null,
};

CollapsableTableRow.propTypes = {
  data: PropTypes.array,
  label: PropTypes.string,
  altLabel: PropTypes.string,
  subLabel: PropTypes.string,
  className: PropTypes.string,
  rowResource: PropTypes.object.isRequired,
  rowSets: PropTypes.array,
  rowSummarySet: PropTypes.array,
  isRowSet: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  cellType: PropTypes.string,
  rowDetailsUrl: PropTypes.string,
  onCellFormSubmitOverride: PropTypes.func,
  onLockRow: PropTypes.func,
};

export default withStyles(styles)(CollapsableTableRow);
